import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'echo-portal-search-history';
const MAX_ITEMS = 5;
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

export interface RecentSearch {
  query: string;
  lastUsed: number; // epoch ms
}

interface StoredShape {
  items: RecentSearch[];
}

function readFromStorage(): RecentSearch[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredShape;
    const now = Date.now();
    return (parsed.items ?? [])
      .filter((it) => typeof it.query === 'string' && it.query.trim().length > 0)
      .filter((it) => typeof it.lastUsed === 'number' && now - it.lastUsed < MAX_AGE_MS)
      .slice(0, MAX_ITEMS);
  } catch {
    return [];
  }
}

function writeToStorage(items: RecentSearch[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ items }));
  } catch {
    // Quota / private mode — silently drop. Recents are best-effort.
  }
}

/**
 * Persisted "recent searches" — last `MAX_ITEMS` (5) queries the user
 * activated (clicked or hit Enter on). Deduped (case-insensitive,
 * move-to-front on re-use) and pruned by `MAX_AGE_MS` (90 days).
 *
 * Storage key: `echo-portal-search-history` (follows the project's
 * `echo-portal-*` localStorage prefix convention).
 */
export function useRecentSearches() {
  const [items, setItems] = useState<RecentSearch[]>(() => readFromStorage());

  // Persist after every state change. We do this in an effect rather than
  // inside the setState updater because React strict mode runs updaters
  // twice (to surface side-effect bugs); writing to localStorage there
  // would race when batched calls are reconciled.
  useEffect(() => {
    writeToStorage(items);
  }, [items]);

  const push = useCallback((rawQuery: string) => {
    const q = rawQuery.trim();
    if (q.length === 0) return;
    setItems((prev) => {
      const lower = q.toLowerCase();
      const filtered = prev.filter((it) => it.query.toLowerCase() !== lower);
      return [{ query: q, lastUsed: Date.now() }, ...filtered].slice(0, MAX_ITEMS);
    });
  }, []);

  const remove = useCallback((rawQuery: string) => {
    const lower = rawQuery.trim().toLowerCase();
    setItems((prev) => prev.filter((it) => it.query.toLowerCase() !== lower));
  }, []);

  const clear = useCallback(() => {
    setItems([]);
  }, []);

  return { items, push, remove, clear };
}
